import { FC } from 'react';
import { Checkbox, Divider, InputNumber, Space } from 'antd';

import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import { useEntitlements } from '@/hooks/useEntitlements';
import { PlanTag } from '@/shared';
import { PlatformForm as Form, PlatformSelect as Select } from '@/shared/components/forms';

interface FundamentalsProps {
    filters: any;
    handleChange: any;
}

type ScreenerRangeField = 'beta' | 'dividend' | 'marketCap' | 'price' | 'volume';
type ScreenerBooleanField = 'includeAllShareClasses' | 'isActivelyTrading' | 'isEtf' | 'isFund';

const revenueRanges = [
    '$0 - $1M',
    '$1M - $10M',
    '$10M - $50M',
    '$50M - $100M',
    '$100M - $250M',
    '$250M - $500M',
    '$500M - $1B',
    '$1B - $10B',
    '$10B+',
];

const rangeFields: Array<{ field: ScreenerRangeField; label: string }> = [
    { field: 'marketCap', label: 'Market Capitalization' },
    { field: 'price', label: 'Share Price' },
    { field: 'volume', label: 'Trading Volume' },
    { field: 'beta', label: 'Beta' },
    { field: 'dividend', label: 'Dividend' },
];

const booleanFields: Array<{ field: ScreenerBooleanField; label: string }> = [
    { field: 'isActivelyTrading', label: 'Actively Trading' },
    { field: 'isEtf', label: 'ETF' },
    { field: 'isFund', label: 'Fund' },
    { field: 'includeAllShareClasses', label: 'Include All Share Classes' },
];

const booleanOptions = [
    { label: 'Any', value: 'any' },
    { label: 'Yes', value: 'true' },
    { label: 'No', value: 'false' },
];

const Fundamentals: FC<FundamentalsProps> = ({ filters, handleChange }): JSX.Element => {
    const { hasMinimumPlan } = useEntitlements();
    const { token } = useAntDesignTheme();

    if (!hasMinimumPlan('plus')) {
        return (
            <div style={{ textAlign: 'center', margin: '2rem' }}>
                <PlanTag plan='plus' style={{ marginLeft: '0.5rem' }} />
            </div>
        );
    }

    const booleanValue = (field: ScreenerBooleanField) => {
        const value = filters?.screener?.[field];

        return value === undefined ? 'any' : String(value);
    };

    return (
        <Form layout='vertical'>
            <small style={{ color: token.colorTextTertiary }}>FMP Stock Screener Metrics</small>
            <div style={{ marginTop: '0.5rem' }}>
                {rangeFields.map(({ field, label }) => (
                    <Form.Item key={field} label={label} style={{ marginBottom: '0.5rem' }}>
                        <Space.Compact block>
                            <InputNumber
                                aria-label={`Minimum ${label}`}
                                controls={false}
                                min={field === 'beta' ? undefined : 0}
                                placeholder='Minimum...'
                                style={{ width: '50%' }}
                                value={filters?.screener?.[field]?.min}
                                onChange={(value) => handleChange(['screener', field, 'min'], value ?? undefined)}
                            />
                            <InputNumber
                                aria-label={`Maximum ${label}`}
                                controls={false}
                                min={field === 'beta' ? undefined : 0}
                                placeholder='Maximum...'
                                style={{ width: '50%' }}
                                value={filters?.screener?.[field]?.max}
                                onChange={(value) => handleChange(['screener', field, 'max'], value ?? undefined)}
                            />
                        </Space.Compact>
                    </Form.Item>
                ))}
            </div>

            <Divider style={{ margin: '0.75rem 0' }} />
            <small style={{ color: token.colorTextTertiary }}>Security Characteristics</small>
            <div style={{ marginTop: '0.5rem' }}>
                {booleanFields.map(({ field, label }) => (
                    <Form.Item key={field} label={label} style={{ marginBottom: '0.5rem' }}>
                        <Select
                            options={booleanOptions}
                            value={booleanValue(field)}
                            onChange={(value) =>
                                handleChange(['screener', field], value === 'any' ? undefined : value === 'true')
                            }
                        />
                    </Form.Item>
                ))}
            </div>

            <Divider style={{ margin: '0.75rem 0' }} />
            <small style={{ color: token.colorTextTertiary }}>Annual Revenue (TTM, USD)</small>
            <Checkbox.Group
                style={{ marginTop: '0.5rem' }}
                value={filters?.fundamentals?.annualRevenue}
                onChange={(checkedValues) => handleChange(['fundamentals', 'annualRevenue'], checkedValues)}
            >
                {revenueRanges.map((range) => (
                    <div key={range} style={{ width: '100%' }}>
                        <Checkbox value={range}>{range}</Checkbox>
                    </div>
                ))}
            </Checkbox.Group>
        </Form>
    );
};

export default Fundamentals;
